import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { Link } from 'react-router-dom';
import SEO from '../../components/feature/SEO';

const siteUrl = import.meta.env.VITE_SITE_URL ?? '';

const packagesJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Home Safety Packages & Pricing - SafeStay Solutions',
    description:
      'Flexible home safety modification packages in Beverly, MA. Essential Safety from $1,200, Complete Protection from $3,500, and custom Full Home Transformation options.',
    url: `${siteUrl}/packages`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
        { '@type': 'ListItem', position: 2, name: 'Packages', item: `${siteUrl}/packages` },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'SafeStay Safety Packages',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@type': 'Offer',
          name: 'Essential Safety Package',
          description: 'Professional home safety assessment, up to 4 grab bars, basic bathroom safety, and non-slip treatment.',
          price: '1200',
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          seller: { '@type': 'LocalBusiness', name: 'SafeStay Solutions' },
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@type': 'Offer',
          name: 'Complete Protection Package',
          description: 'Everything in Essential Safety plus premium grab bars, lever door handles, doorway clearance, and motion lighting.',
          price: '3500',
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          seller: { '@type': 'LocalBusiness', name: 'SafeStay Solutions' },
        },
      },
      {
        '@type': 'ListItem',
        position: 3,
        item: {
          '@type': 'Offer',
          name: 'Full Home Transformation',
          description: 'Complete home accessibility solution with unlimited grab bars, walk-in tub, ramps, stair lift, and smart home integration.',
          availability: 'https://schema.org/InStock',
          seller: { '@type': 'LocalBusiness', name: 'SafeStay Solutions' },
        },
      },
    ],
  },
];

export default function PackagesPage() {
  const packages = [
    {
      name: 'Essential Safety',
      price: 'Starting at $1,200',
      description: 'Perfect for addressing immediate safety concerns in key areas of your home.',
      features: [
        'Professional home safety assessment',
        'Up to 4 grab bar installations',
        'Basic bathroom safety modifications',
        'Non-slip surface treatment (1 area)',
        'Lighting assessment and recommendations',
        'Safety checklist and report',
      ],
      popular: false,
      color: 'from-gray-50 to-gray-100',
      buttonColor: 'bg-[#1A2B4A] hover:bg-[#152238]',
    },
    {
      name: 'Complete Protection',
      price: 'Starting at $3,500',
      description: 'Comprehensive safety modifications for multiple rooms and enhanced mobility.',
      features: [
        'Everything in Essential Safety Package',
        'Premium Grab Bars (2 additional)',
        '4 Lever-Style door handles',
        'Enhanced doorway clearance for 2 doors',
        'Motion Lighting in high traffic area',
        'Low profile transitions in high traffic areas',
        'Additional stair handrail',
      ],
      popular: true,
      color: 'from-[#D4AF37]/10 to-[#D4AF37]/5',
      buttonColor: 'bg-[#D4AF37] hover:bg-[#C4A137]',
    },
    {
      name: 'Full Home Transformation',
      price: 'Custom Quote',
      description: 'Complete home accessibility solution with advanced modifications and equipment.',
      features: [
        'Everything in Complete Protection',
        'Unlimited grab bar installations',
        'Walk-in tub or roll-in shower installation',
        'Multiple doorway widening',
        'Exterior ramp installation',
        'Stair lift or platform lift installation',
        'Additional stair handrail',
        'Smart home safety integration',
        'Whole-home lighting upgrade',
        'Custom carpentry and modifications',
        '1-year maintenance package',
      ],
      popular: false,
      color: 'from-[#1A2B4A]/5 to-[#1A2B4A]/10',
      buttonColor: 'bg-[#1A2B4A] hover:bg-[#152238]',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Home Safety Packages & Pricing Beverly MA - SafeStay Solutions"
        description="Choose from our home safety packages in Beverly, MA. Essential Safety from $1,200, Complete Protection from $3,500, or custom Full Home Transformation. Licensed & insured contractors."
        keywords="home safety packages Beverly MA, accessibility modification pricing, grab bar installation cost, aging in place packages"
        canonical={`${siteUrl}/packages`}
        ogUrl={`${siteUrl}/packages`}
        jsonLd={packagesJsonLd}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#1A2B4A] to-[#2A3B5A]">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block bg-[#D4AF37]/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <span className="text-[#D4AF37] text-sm font-semibold tracking-wide">FLEXIBLE PRICING OPTIONS</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Safety Packages Designed for Your Needs
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 leading-relaxed">
              Choose the right level of protection for your home. All packages include professional installation, licensed contractors, and our quality guarantee.
            </p>
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <div
                key={index}
                className={`relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 ${
                  pkg.popular ? 'lg:scale-105 border-2 border-[#D4AF37]' : 'border border-gray-200'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute top-0 right-0 bg-[#D4AF37] text-[#1A2B4A] px-4 py-2 text-sm font-bold rounded-bl-lg">
                    MOST POPULAR
                  </div>
                )}
                
                <div className={`bg-gradient-to-br ${pkg.color} p-8`}>
                  <h3 className="text-2xl font-bold text-[#1A2B4A] mb-3">{pkg.name}</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-[#D4AF37]">{pkg.price}</span>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">{pkg.description}</p>
                </div>

                <div className="p-8 bg-white">
                  <ul className="space-y-4 mb-8">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-lg text-[#D4AF37]"></i>
                        </div>
                        <span className="text-gray-700 text-sm leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/free-assessment"
                    className={`block w-full ${pkg.buttonColor} text-white text-center px-6 py-4 rounded-full font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap`}
                  >
                    Get Started
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Info */}
          <div className="mt-16 text-center max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-[#D4AF37]/10 to-[#D4AF37]/5 rounded-2xl p-8 border border-[#D4AF37]/20">
              <h3 className="text-2xl font-bold text-[#1A2B4A] mb-4">Need a Custom Solution?</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Every home is unique, and so are your safety needs. We offer fully customized packages tailored to your specific requirements and budget. Our team will work with you to create the perfect solution.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  href="tel:6175396650"
                  className="inline-flex items-center gap-2 text-[#1A2B4A] font-semibold hover:text-[#D4AF37] transition-colors cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  (617) 539-6650
                </a>
                <span className="hidden sm:block text-gray-400">|</span>
                <a
                  href="mailto:safestayma@gmail.com"
                  className="inline-flex items-center gap-2 text-[#1A2B4A] font-semibold hover:text-[#D4AF37] transition-colors cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  safestayma@gmail.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37]/10 rounded-full mx-auto mb-4">
                <i className="ri-shield-check-line text-3xl text-[#D4AF37]"></i>
              </div>
              <h4 className="text-lg font-bold text-[#1A2B4A] mb-2">Licensed &amp; Insured</h4>
              <p className="text-gray-600 text-sm">Fully certified professionals you can trust</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37]/10 rounded-full mx-auto mb-4">
                <i className="ri-time-line text-3xl text-[#D4AF37]"></i>
              </div>
              <h4 className="text-lg font-bold text-[#1A2B4A] mb-2">Fast Installation</h4>
              <p className="text-gray-600 text-sm">Most projects completed within 1-3 days</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37]/10 rounded-full mx-auto mb-4">
                <i className="ri-star-line text-3xl text-[#D4AF37]"></i>
              </div>
              <h4 className="text-lg font-bold text-[#1A2B4A] mb-2">Quality Guaranteed</h4>
              <p className="text-gray-600 text-sm">Workmanship warranty on all installations</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1A2B4A] to-[#2A3B5A] relative">
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/10"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Make Your Home Safer?
          </h2>
          <p className="text-lg sm:text-xl text-gray-200 mb-8 leading-relaxed">
            Schedule your free home safety assessment today. We'll evaluate your needs and recommend the best package for your situation—no obligation.
          </p>
          <Link
            to="/free-assessment"
            className="inline-block bg-[#D4AF37] text-[#1A2B4A] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#C4A137] transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
          >
            Schedule Free Assessment
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
